import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PAYROLL_RUN_STATUS_MAP, type PayrollRunStatus } from '@goyone/shared';
import { api } from '../../lib/api';
import { money } from '../../lib/format';
import { useListState } from '../../lib/hooks';
import {
  Badge,
  EmptyState,
  ErrorState,
  PageHead,
  SearchBox,
  Stat,
  TableSkeleton,
} from '../../components/ui';
import { DateRangeFilter } from '../../components/DateRangeFilter';
import { ExportMenu } from '../../components/ExportMenu';
import {
  describeFilters,
  FilterButton,
  FilterChips,
  FilterScope,
  HeaderFilter,
  matchesSearch,
  useLocalFilterTable,
  type FilterDef,
  type LocalAccessors,
} from '../../components/ColumnFilter';
import { exportNotes, exportPeriod, type TableExportDoc } from '../../lib/tableExport';

interface Run {
  id: string;
  period: string;
  periodLabel: string;
  status: PayrollRunStatus;
  total: number;
  assetCount: number;
  journalEntryId: string | null;
}

interface PreviewLine {
  assetId: string;
  assetCode: string;
  assetName: string;
  categoryName: string;
  accumulatedBefore: number;
  amount: number;
  bookValueAfter: number;
  behind: boolean;
  firstDue: string;
}

interface Preview {
  period: string;
  periodLabel: string;
  alreadyRun: boolean;
  runId: string | null;
  total: number;
  lines: PreviewLine[];
  behind: PreviewLine[];
}

/** أول يوم في الشهر الماضي — أقرب شهر مكتمل. */
function lastMonth(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1))
    .toISOString()
    .slice(0, 7);
}

/* ==========================================================================
   فلاتر الجداول — محلّية: الدورات وأقساطها تصل كاملة
   ========================================================================== */

const RUN_STATES = [
  { value: 'DRAFT', label: 'مسودة' },
  { value: 'POSTED', label: 'مرحَّلة' },
];

const RUN_FILTERS: FilterDef[] = [
  { key: 'period', label: 'الشهر', kind: 'date', sortable: true },
  { key: 'assetCount', label: 'الأصول', kind: 'number', sortable: true },
  { key: 'total', label: 'إجمالي الإهلاك', kind: 'number', sortable: true },
  { key: 'status', label: 'الحالة', kind: 'enum', options: RUN_STATES },
  {
    key: 'journal',
    label: 'الدفتر',
    kind: 'enum',
    options: [
      { value: 'yes', label: 'مرحَّل' },
      { value: 'no', label: 'غير مرحَّل' },
    ],
  },
];

const RUN_ACCESSORS: LocalAccessors<Run> = {
  /* الحالات ثلاث في الرواتب، والإهلاك منها اثنتان: مسودة وما بعدها مرحَّل. */
  status: (r) => (r.status === 'DRAFT' ? 'DRAFT' : 'POSTED'),
  journal: (r) => (r.journalEntryId ? 'yes' : 'no'),
};

/** أعمدة الأقساط — في المعاينة وفي تفاصيل الدورة. */
const LINE_FILTERS: FilterDef[] = [
  { key: 'asset', label: 'الأصل', kind: 'text', sortable: true },
  { key: 'categoryName', label: 'التصنيف', kind: 'text', sortable: true },
  { key: 'accumulatedBefore', label: 'المجمّع قبل', kind: 'number', sortable: true },
  { key: 'amount', label: 'قسط الشهر', kind: 'number', sortable: true },
  { key: 'bookValueAfter', label: 'الدفترية بعد', kind: 'number', sortable: true },
];

const LINE_ACCESSORS: LocalAccessors<PreviewLine> = {
  asset: (l) => l.assetName,
};

/* تفاصيل الدورة لا تعرض المجمّع قبل القسط. */
const DETAIL_FILTERS = LINE_FILTERS.filter((d) => d.key !== 'accumulatedBefore');

const lineSearch = (term: string) =>
  term ? (l: PreviewLine) => matchesSearch(term, l.assetName, l.assetCode, l.categoryName) : undefined;

/** تصدير الأقساط المعروضة. */
function linesExport(title: string, lines: PreviewLine[], notes: string[]): TableExportDoc {
  return {
    title,
    sheets: [
      {
        title,
        sections: [
          {
            columns: [
              { key: 'code', label: 'الكود' },
              { key: 'name', label: 'الأصل' },
              { key: 'category', label: 'التصنيف' },
              { key: 'before', label: 'المجمّع قبل', kind: 'money' },
              { key: 'amount', label: 'قسط الشهر', kind: 'money', total: true },
              { key: 'after', label: 'الدفترية بعد', kind: 'money' },
            ],
            rows: lines.map((l) => ({
              code: l.assetCode,
              name: l.assetName,
              category: l.categoryName,
              before: l.accumulatedBefore,
              amount: l.amount,
              after: l.bookValueAfter,
            })),
          },
        ],
        notes,
      },
    ],
  };
}

/* ==========================================================================
   قائمة الدورات
   ========================================================================== */

export function DepreciationRuns() {
  const queryClient = useQueryClient();
  const [period, setPeriod] = useState(lastMonth());

  const runs = useQuery({
    queryKey: ['depreciation', 'runs'],
    queryFn: () => api<Run[]>('/assets/depreciation/runs'),
  });

  const preview = useQuery({
    queryKey: ['depreciation', 'preview', period],
    queryFn: () => api<Preview>(`/assets/depreciation/preview?period=${period}-01`),
  });

  const create = useMutation({
    mutationFn: () =>
      api<Run>('/assets/depreciation/runs', {
        method: 'POST',
        body: { period: `${period}-01` },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['depreciation'] }),
  });

  const p = preview.data;

  /*
   * جدولان في الصفحة: الدورات بمعاملات الرابط المعتادة (`f`، `q`، `from`،
   * `to`)، ومعاينة الأقساط بمعامل `p` وبحث `pq` فلا يختلطان.
   */
  const { state, patch } = useListState();
  const [search, setSearch] = useState(state.q ?? '');
  const [pSearch, setPSearch] = useState(state.pq ?? '');
  const from = state.from;
  const to = state.to;

  const runsTable = useLocalFilterTable({
    rows: runs.data,
    defs: RUN_FILTERS,
    accessors: RUN_ACCESSORS,
    state,
    patch,
    /*
     * مدى «من/إلى» بالشهر: الدورة شهر، فتدخل إن وقع شهرها بين شهري الطرفين
     * — «من ١٥ مارس» يشمل دورة مارس.
     */
    search:
      search || from || to
        ? (r: Run) =>
            matchesSearch(search, r.periodLabel, r.period) &&
            (!from || r.period.slice(0, 7) >= from.slice(0, 7)) &&
            (!to || r.period.slice(0, 7) <= to.slice(0, 7))
        : undefined,
  });
  const runRows = runsTable.rows;

  const previewTable = useLocalFilterTable({
    rows: p?.lines,
    defs: LINE_FILTERS,
    accessors: LINE_ACCESSORS,
    state,
    patch,
    param: 'p',
    search: lineSearch(pSearch),
  });

  const buildRunsExport = (): TableExportDoc => ({
    title: 'دورات الإهلاك',
    sheets: [
      {
        title: 'دورات الإهلاك',
        period: exportPeriod(from, to),
        sections: [
          {
            columns: [
              { key: 'period', label: 'الشهر' },
              { key: 'assetCount', label: 'الأصول', kind: 'number' },
              { key: 'total', label: 'إجمالي الإهلاك', kind: 'money', total: true },
              { key: 'status', label: 'الحالة' },
            ],
            rows: runRows.map((r) => ({
              period: r.periodLabel,
              assetCount: r.assetCount,
              total: r.total,
              status: r.status === 'DRAFT' ? 'مسودة' : 'مرحَّلة',
            })),
          },
        ],
        notes: exportNotes({
          q: search,
          conditions: describeFilters(RUN_FILTERS, runsTable.filters),
          count: runRows.length,
          total: runRows.length,
          truncated: false,
          unit: 'الدورات',
        }),
      },
    ],
  });

  return (
    <div className="page">
      <PageHead
        title="إهلاك الأصول"
        subtitle="القسط الشهري ومرحّله إلى الدفتر العام"
        actions={
          <Link to="/assets" className="btn">
            سجل الأصول
          </Link>
        }
      />

      <div className="card">
        <div className="card-head">
          <h2 className="card-title">دورة جديدة</h2>
        </div>
        <div className="card-body">
          <div className="row" style={{ gap: 8, alignItems: 'flex-end' }}>
            <div className="field" style={{ margin: 0 }}>
              <label className="label" htmlFor="d-period">
                الشهر
              </label>
              <input
                id="d-period"
                className="input num"
                type="month"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
              />
            </div>
            <button
              className="btn btn-primary"
              onClick={() => create.mutate()}
              disabled={
                create.isPending || !p || p.alreadyRun || p.lines.length === 0 || p.behind.length > 0
              }
            >
              {create.isPending ? 'جارٍ الحساب' : 'إنشاء الدورة'}
            </button>
          </div>

          {create.isError && (
            <div className="alert alert-danger mt-4">
              {create.error instanceof Error ? create.error.message : 'تعذّر الإنشاء.'}
            </div>
          )}

          {p?.alreadyRun && (
            <div className="alert alert-info mt-4">
              دورة {p.periodLabel} موجودة بالفعل.{' '}
              {p.runId && (
                <Link to={`/assets/depreciation/${p.runId}`} className="link">
                  افتحها
                </Link>
              )}
            </div>
          )}

          {/*
            الأصول المتأخرة تُعرض ولا تُهمَل.
            الشهور تُرحَّل بالترتيب: القسط المتناقص يُحسب من القيمة الدفترية
            المتبقية، فقفز شهر يعطي رقمًا أكبر من الصحيح ويدخل الدفتر.
          */}
          {p && p.behind.length > 0 && (
            <div className="alert alert-warning mt-4">
              <div>
                {p.behind.length} أصلًا له شهر أقدم لم يُرحَّل. الشهور تُهلَك بالترتيب، فابدأ
                بـ{p.behind[0]!.firstDue.slice(0, 7)} أولًا.
              </div>
            </div>
          )}

          {p && !p.alreadyRun && p.lines.length > 0 && p.behind.length === 0 && (
            <>
              <div className="stats mt-4">
                <Stat label="أصول مستحقة" value={p.lines.length} />
                <Stat label="إجمالي القسط" value={<span className="num">{money(p.total)}</span>} />
              </div>

              <div className="toolbar mt-4">
                <SearchBox
                  value={pSearch}
                  onChange={(v) => {
                    setPSearch(v);
                    patch({ pq: v || undefined });
                  }}
                  placeholder="بحث بالأصل أو كوده أو تصنيفه"
                />
                <ExportMenu
                  build={() =>
                    linesExport(
                      `معاينة إهلاك ${p.periodLabel}`,
                      previewTable.rows,
                      exportNotes({
                        q: pSearch,
                        conditions: describeFilters(LINE_FILTERS, previewTable.filters),
                        count: previewTable.rows.length,
                        total: p.lines.length,
                        truncated: false,
                        unit: 'الأصول',
                      }),
                    )
                  }
                  disabled={!previewTable.rows.length}
                />
                <span className="row" style={{ marginInlineStart: 'auto', gap: 'var(--sp-2)' }}>
                  <FilterButton {...previewTable.button} />
                </span>
              </div>
              <FilterChips {...previewTable.chips} />

              <FilterScope {...previewTable.scope}>
              <div className="table-wrap sticky-head mt-4">
                <table className="table">
                  <thead>
                    <tr>
                      <th>
                        الأصل
                        <HeaderFilter colKey="asset" />
                      </th>
                      <th>
                        التصنيف
                        <HeaderFilter colKey="categoryName" />
                      </th>
                      <th>
                        المجمّع قبل
                        <HeaderFilter colKey="accumulatedBefore" />
                      </th>
                      <th>
                        قسط الشهر
                        <HeaderFilter colKey="amount" />
                      </th>
                      <th>
                        الدفترية بعد
                        <HeaderFilter colKey="bookValueAfter" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewTable.rows.map((l) => (
                      <tr key={l.assetId}>
                        <td>
                          <span className="cell-strong">{l.assetName}</span>
                          <div className="tiny mute num" dir="ltr">
                            {l.assetCode}
                          </div>
                        </td>
                        <td className="cell-mute">{l.categoryName}</td>
                        <td className="num cell-mute">{money(l.accumulatedBefore)}</td>
                        <td className="num neg">{money(l.amount)}</td>
                        <td className="num">{money(l.bookValueAfter)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {previewTable.rows.length === 0 && (
                  <p className="mute small center" style={{ padding: 'var(--sp-4)' }}>
                    لا أصل يطابق هذا البحث أو الفلتر.
                  </p>
                )}
              </div>
              </FilterScope>
            </>
          )}

          {p && !p.alreadyRun && p.lines.length === 0 && p.behind.length === 0 && (
            <p className="mute mt-4">
              لا أصول مستحقة الإهلاك في {p.periodLabel} — إما لم تُسجَّل أصول، أو أُهلكت
              بالكامل.
            </p>
          )}
        </div>
      </div>

      <div className="card mt-4">
        <div className="card-head">
          <h2 className="card-title">الدورات السابقة</h2>
        </div>

        {runs.isError && <ErrorState error={runs.error} onRetry={() => runs.refetch()} />}

        <div className="toolbar" style={{ paddingInline: 'var(--sp-4)' }}>
          <SearchBox
            value={search}
            onChange={(v) => {
              setSearch(v);
              patch({ q: v || undefined });
            }}
            placeholder="بحث بالشهر"
          />
          <DateRangeFilter from={from} to={to} onChange={(f, t) => patch({ from: f, to: t })} />
          <ExportMenu build={buildRunsExport} disabled={!runRows.length} />
          <span className="row" style={{ marginInlineStart: 'auto', gap: 'var(--sp-2)' }}>
            <FilterButton {...runsTable.button} />
          </span>
        </div>
        <FilterChips {...runsTable.chips} />

        <FilterScope {...runsTable.scope}>
        <div className="table-wrap sticky-head">
          <table className="table">
            <thead>
              <tr>
                <th>
                  الشهر
                  <HeaderFilter colKey="period" />
                </th>
                <th>
                  الأصول
                  <HeaderFilter colKey="assetCount" />
                </th>
                <th>
                  إجمالي الإهلاك
                  <HeaderFilter colKey="total" />
                </th>
                <th>
                  الحالة
                  <HeaderFilter colKey="status" />
                </th>
                <th>
                  الدفتر
                  <HeaderFilter colKey="journal" />
                </th>
              </tr>
            </thead>

            {runs.isLoading && <TableSkeleton rows={4} cols={5} />}

            <tbody>
              {runRows.map((r) => (
                <tr key={r.id}>
                  <td>
                    <Link to={`/assets/depreciation/${r.id}`} className="cell-strong link">
                      {r.periodLabel}
                    </Link>
                  </td>
                  <td className="num cell-mute">{r.assetCount}</td>
                  <td className="num">{money(r.total)}</td>
                  <td>
                    <Badge kind={PAYROLL_RUN_STATUS_MAP[r.status].kind}>
                      {r.status === 'DRAFT' ? 'مسودة' : 'مرحَّلة'}
                    </Badge>
                  </td>
                  <td className="cell-mute">{r.journalEntryId ? 'مرحَّل' : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </FilterScope>

        {!runs.isLoading && runs.data?.length === 0 && (
          <EmptyState
            title="لا دورات إهلاك بعد"
            text="بلا إهلاك مرحَّل تخلو قائمة الدخل من مصروف حقيقي، فيظهر الربح أعلى مما هو."
          />
        )}
        {!!runs.data?.length && runRows.length === 0 && (
          <EmptyState title="لا نتائج" text="لا دورة تطابق هذا البحث أو المدى أو الفلتر." />
        )}
      </div>
    </div>
  );
}

/* ==========================================================================
   تفاصيل الدورة
   ========================================================================== */

export function DepreciationRunDetail() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();

  const run = useQuery({
    queryKey: ['depreciation', 'run', id],
    queryFn: () =>
      api<Run & { entries: Array<PreviewLine & { bookValueAfter: number }> }>(
        `/assets/depreciation/runs/${id}`,
      ),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['depreciation'] });

  const { state, patch } = useListState();
  const [search, setSearch] = useState(state.q ?? '');
  const table = useLocalFilterTable({
    rows: run.data?.entries,
    defs: DETAIL_FILTERS,
    accessors: LINE_ACCESSORS,
    state,
    patch,
    search: lineSearch(search),
  });

  const post = useMutation({
    mutationFn: () => api(`/assets/depreciation/runs/${id}/post`, { method: 'POST' }),
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: () => api(`/assets/depreciation/runs/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      refresh();
      window.location.href = '/assets/depreciation';
    },
  });

  if (run.isError) return <ErrorState error={run.error} onRetry={() => run.refetch()} />;
  const r = run.data;

  return (
    <div className="page">
      <PageHead
        title={r ? `إهلاك ${r.periodLabel}` : 'دورة إهلاك'}
        subtitle={r ? `${r.assetCount} أصلًا` : undefined}
        actions={
          r && (
            <>
              <Link to="/assets/depreciation" className="btn">
                رجوع
              </Link>
              {r.status === 'DRAFT' && (
                <>
                  <button className="btn btn-danger" onClick={() => remove.mutate()}>
                    حذف المسودة
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => post.mutate()}
                    disabled={post.isPending}
                  >
                    {post.isPending ? 'جارٍ الترحيل' : 'ترحيل إلى الدفتر'}
                  </button>
                </>
              )}
            </>
          )
        }
      />

      {(post.isError || remove.isError) && (
        <div className="alert alert-danger mb-4">
          {[post.error, remove.error].find(Boolean) instanceof Error
            ? ([post.error, remove.error].find(Boolean) as Error).message
            : 'تعذّرت العملية.'}
        </div>
      )}

      {r && (
        <>
          <div className="stats">
            <Stat
              label="الحالة"
              value={
                <Badge kind={r.status === 'DRAFT' ? 'neutral' : 'success'}>
                  {r.status === 'DRAFT' ? 'مسودة' : 'مرحَّلة'}
                </Badge>
              }
            />
            <Stat label="إجمالي الإهلاك" value={<span className="num">{money(r.total)}</span>} />
            <Stat label="الأصول" value={r.assetCount} />
          </div>

          {r.status === 'DRAFT' ? (
            <div className="alert alert-info mb-4">
              مسودة لم تدخل الدفتر بعد. راجع الأقساط ثم رحّلها — بعد الترحيل تُقفل ولا تُحذف،
              ويُصحَّح الخطأ بقيد عاكس.
            </div>
          ) : (
            <div className="alert alert-info mb-4">
              {/*
                القيد الفعلي: من مصروف الإهلاك إلى مجمع الإهلاك.
                تاريخه آخر يوم في الشهر لأن الإهلاك استهلاك خلاله كله.
              */}
              رُحِّلت إلى الدفتر بقيد من حساب مصروف الإهلاك إلى حساب مجمع الإهلاك، بتاريخ آخر
              يوم في {r.periodLabel}.
            </div>
          )}

          <div className="toolbar sticky-bar">
            <SearchBox
              value={search}
              onChange={(v) => {
                setSearch(v);
                patch({ q: v || undefined });
              }}
              placeholder="بحث بالأصل أو كوده أو تصنيفه"
            />
            <ExportMenu
              build={() =>
                linesExport(
                  `إهلاك ${r.periodLabel}`,
                  table.rows,
                  exportNotes({
                    q: search,
                    conditions: describeFilters(DETAIL_FILTERS, table.filters),
                    count: table.rows.length,
                    total: r.entries.length,
                    truncated: false,
                    unit: 'الأصول',
                  }),
                )
              }
              disabled={!table.rows.length}
            />
            <span className="row" style={{ marginInlineStart: 'auto', gap: 'var(--sp-2)' }}>
              <FilterButton {...table.button} />
            </span>
          </div>
          <FilterChips {...table.chips} />

          <FilterScope {...table.scope}>
          <div className="card">
            <div className="table-wrap sticky-head">
              <table className="table">
                <thead>
                  <tr>
                    <th>
                      الأصل
                      <HeaderFilter colKey="asset" />
                    </th>
                    <th>
                      التصنيف
                      <HeaderFilter colKey="categoryName" />
                    </th>
                    <th>
                      قسط الشهر
                      <HeaderFilter colKey="amount" />
                    </th>
                    <th>
                      القيمة الدفترية بعده
                      <HeaderFilter colKey="bookValueAfter" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {table.rows.map((e) => (
                    <tr key={e.assetId}>
                      <td>
                        <Link to={`/assets/${e.assetId}`} className="cell-strong link">
                          {e.assetName}
                        </Link>
                        <div className="tiny mute num" dir="ltr">
                          {e.assetCode}
                        </div>
                      </td>
                      <td className="cell-mute">{e.categoryName}</td>
                      <td className="num neg">{money(e.amount)}</td>
                      <td className="num">{money(e.bookValueAfter)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {r.entries.length > 0 && table.rows.length === 0 && (
              <EmptyState title="لا نتائج" text="لا أصل يطابق هذا البحث أو الفلتر." />
            )}
          </div>
          </FilterScope>
        </>
      )}
    </div>
  );
}
